import { useState, useEffect } from 'react';
import { generateUUID } from '@workspace/utils';
import { FormStructure, FormField } from '../features/surveys/types';
import { SurveyFieldEditor } from './SurveyFieldEditor';

interface Props {
  initialStructure?: FormStructure;
  onSubmit: (structure: FormStructure, isDraft?: boolean) => void;
  onCancel?: () => void;
  error?: string;
  isPublished?: boolean;
  labels?: {
    title?: string;
    addField?: string;
    emptyText?: string;
    addFirstField?: string;
    saveDraft?: string;
    submit?: string;
  };
  showGroups?: boolean;
}

export function SurveyFormBuilder({
  initialStructure,
  onSubmit,
  onCancel,
  error,
  isPublished,
  labels,
  showGroups = true,
}: Props) {
  const generateFieldId = () => {
    return `field-${Date.now()}-${generateUUID().slice(0, 8)}`;
  };

  // Preservar IDs existentes al editar (evita que respuestas antiguas muestren "-")
  // Solo generar nuevos IDs para campos añadidos en addField()
  const [fields, setFields] = useState<FormField[]>(
    initialStructure?.fields ? [...initialStructure.fields] : []
  );

  useEffect(() => {
    if (initialStructure?.fields) {
      setFields([...initialStructure.fields]);
    }
  }, [initialStructure]);

  const addField = () => {
    const newField: FormField = {
      id: generateFieldId(),
      type: 'text',
      label: '',
      required: false,
    };
    setFields([...fields, newField]);
  };

  const updateField = (index: number, updatedField: FormField) => {
    const newFields = [...fields];
    newFields[index] = updatedField;
    setFields(newFields);
  };

  const deleteField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < fields.length) {
      [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
      setFields(newFields);
    }
  };

  const getStructure = (): FormStructure => {
    return {
      fields: fields.map(({ id, type, label, required, placeholder, options, validation, groupId }) => ({
        id,
        type,
        label,
        required,
        ...(placeholder && { placeholder }),
        ...(options && options.length > 0 && { options }),
        ...(validation && Object.keys(validation).length > 0 && { validation }),
        ...(groupId && { groupId }),
      })),
    };
  };

  const handleSaveDraft = () => {
    onSubmit(getStructure(), true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validaciones (solo para publicar)
    if (fields.length === 0) {
      alert('Debes agregar al menos un campo al formulario para publicarlo');
      return;
    }

    const typesWithOptions = ['select', 'radio', 'checkbox-multiple'];
    const fieldsWithErrors = fields.filter(
      (f) => !f.label || (typesWithOptions.includes(f.type) && (!f.options || f.options.length === 0))
    );

    if (fieldsWithErrors.length > 0) {
      alert('Para publicar, todos los campos deben tener una etiqueta. Los campos de tipo "select", "radio" y "checkbox-multiple" deben tener al menos una opción.');
      return;
    }

    onSubmit(getStructure(), false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{labels?.title ?? 'Campos del Formulario'}</h3>
          <button
            type="button"
            onClick={addField}
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {labels?.addField ?? '+ Agregar Campo'}
          </button>
        </div>

        {fields.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {labels?.emptyText ?? 'No hay campos en el formulario. Haz clic en "Agregar Campo" para comenzar.'}
            </p>
            <button
              type="button"
              onClick={addField}
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {labels?.addFirstField ?? 'Agregar Primer Campo'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {fields.map((field, index) => (
              <div key={field.id} className="relative">
                <div className="absolute -left-6 top-4 text-sm font-medium text-muted-foreground">
                  {index + 1}
                </div>
                <SurveyFieldEditor
                  field={field}
                  onChange={(updatedField) => updateField(index, updatedField)}
                  onDelete={() => deleteField(index)}
                  onMoveUp={index > 0 ? () => moveField(index, 'up') : undefined}
                  onMoveDown={index < fields.length - 1 ? () => moveField(index, 'down') : undefined}
                  canMoveUp={index > 0}
                  canMoveDown={index < fields.length - 1}
                  showGroups={showGroups}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 pt-4 border-t">
        <button
          type="button"
          onClick={handleSaveDraft}
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
        >
          {labels?.saveDraft ?? 'Guardar como Borrador'}
        </button>
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {labels?.submit ?? (isPublished ? 'Actualizar Encuesta' : 'Publicar Encuesta')}
        </button>
        {onCancel && (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            onClick={onCancel}
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}








